import type { SignedRequest, IdentityId, Nonce, SignerId } from '@shared/types';
import { createSignatureMessage } from '@shared/signatureMessage.ts';
import { signEd25519, signWithSigningKey, type SigningKey } from './ed25519Provider';
import { bytesToBase64 } from '../utils/encoding';

/**
 * Generate random nonce
 */
function generateNonce(): Nonce {
  const nonce = crypto.getRandomValues(new Uint8Array(32));
  return bytesToBase64(nonce);
}

/**
 * Sign request with device private key
 */
export async function signRequest<T>(
  type: string,
  payload: T,
  signerId: SignerId,
  devicePrivateKey: Uint8Array,
  devicePrivateKeyJwk?: JsonWebKey
): Promise<SignedRequest<T>> {
  const timestamp = Date.now();
  const nonce = generateNonce();

  const unsignedRequest = {
    type,
    timestamp,
    nonce,
    signerId,
    payload
  };

  const message = createSignatureMessage(unsignedRequest);
  const messageBytes = new TextEncoder().encode(message);
  const signatureBytes = await signEd25519(messageBytes, devicePrivateKey, devicePrivateKeyJwk);

  return {
    ...unsignedRequest,
    signature: bytesToBase64(signatureBytes)
  };
}

/**
 * Sign request with a non-exportable CryptoKey (preferred path for device signing).
 */
export async function signRequestWithKey<T>(
  type: string,
  payload: T,
  signerId: SignerId,
  signingKey: SigningKey
): Promise<SignedRequest<T>> {
  const timestamp = Date.now();
  const nonce = generateNonce();

  const unsignedRequest = { type, timestamp, nonce, signerId, payload };
  const message = createSignatureMessage(unsignedRequest);
  const messageBytes = new TextEncoder().encode(message);
  const signatureBytes = await signWithSigningKey(messageBytes, signingKey);

  return { ...unsignedRequest, signature: bytesToBase64(signatureBytes) };
}

/**
 * Sign request with identity private key
 */
export async function signIdentityRequest<T>(
  type: string,
  payload: T,
  identityId: IdentityId,
  identityPrivateKey: Uint8Array,
  identityPrivateKeyJwk?: JsonWebKey
): Promise<SignedRequest<T>> {
  // Wire format uses `signerId` for the signer identifier.
  // For identity-signed requests this is the identityId.
  return signRequest(type, payload, identityId, identityPrivateKey, identityPrivateKeyJwk);
}
